import type {
  OrderChannel,
  OrderDTO,
  OrderStatus,
} from "@/modules/orders/domain/contracts";

export const RETAIL_PAYMENT_IN_STORE_LABEL = "ຊຳລະທີ່ຫນ້າຮ້ານ";
export const RETAIL_PAYMENT_SETTLED_MESSAGE = "ຊຳລະຄົບແລ້ວທີ່ຫນ້າຮ້ານ";
export const RETAIL_WALK_IN_SHIPPING_LABEL = "ຮັບທີ່ຮ້ານ";

const BASE_PAYMENT_LABELS: Record<string, string> = {
  cod: "ຊຳລະປາຍທາງ",
  online: "ອອນໄລນ໌",
  cash: "ເງິນສົດ",
  bank_transfer: "ໂອນເງິນ",
};

const RETAIL_PAYMENT_LABELS: Record<string, string> = {
  cash: "ເງິນສົດ",
  bank_transfer: "ໂອນເງິນ",
  cod: RETAIL_PAYMENT_IN_STORE_LABEL, // legacy retail
};

export function isRetailChannel(
  channel: OrderChannel | string | null | undefined,
): boolean {
  return channel === "retail" || channel === "pos";
}

/** POS walk-in — ບໍ່ມີ flow ຂົນສົ່ງ / tracking */
export function isRetailWalkInOrder(
  order: Pick<OrderDTO, "channel" | "shipping" | "trackingNumber">,
): boolean {
  if (!isRetailChannel(order.channel)) return false;
  if (order.trackingNumber?.trim()) return false;

  const shipping = order.shipping;
  if (!shipping) return true;
  if (shipping.shippingCarrierId?.trim()) return false;

  const village = shipping.shippingVillage?.trim() ?? "";
  const recipient = shipping.shippingRecipientName?.trim() ?? "";

  if (recipient === "POS walk-in" || village === "POS") return true;
  if (!village || village === "—") {
    return !shipping.shippingProvinceId && !shipping.shippingDistrictId;
  }

  return false;
}

export function formatOrderPaymentMethodLabel(
  paymentMethod: OrderDTO["paymentMethod"],
  channel: OrderChannel | string | null | undefined,
): string {
  if (isRetailChannel(channel)) {
    if (!paymentMethod) return RETAIL_PAYMENT_IN_STORE_LABEL;
    return (
      RETAIL_PAYMENT_LABELS[paymentMethod] ?? RETAIL_PAYMENT_IN_STORE_LABEL
    );
  }
  if (!paymentMethod) return "—";
  return BASE_PAYMENT_LABELS[paymentMethod] ?? paymentMethod;
}

export function retailPaymentEmptyMessage(order: {
  channel: OrderChannel | string;
  status: OrderStatus;
  payments?: unknown[];
}): string {
  if (!isRetailChannel(order.channel)) {
    return "ບໍ່ມີລາຍການຊຳລະ (COD ຫຼື ຍັງບໍ່ທັນມີ payment)";
  }
  if (
    order.status === "completed" ||
    order.status === "confirmed" ||
    order.status === "cancelled"
  ) {
    return RETAIL_PAYMENT_SETTLED_MESSAGE;
  }
  return `${RETAIL_PAYMENT_IN_STORE_LABEL} (ລໍຖ້າຢືນຢັນ)`;
}

export function retailWalkInStatusLabel(status: OrderStatus): string {
  if (status === "completed") return "ສຳເລັດ · ຂາຍຫນ້າຮ້ານ";
  if (status === "confirmed") return "ຢືນຢັນ · ຂາຍຫນ້າຮ້ານ";
  if (status === "pending_payment") return "ລໍຖ້າຊຳລະ · ຫນ້າຮ້ານ";
  return status;
}
